import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api/endpoints';
import { PageHeader } from '../components/layout/PageHeader';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  Skeleton,
} from '../components/ui/primitives';
import { dateTime } from '../lib/format';

/** Who changed what, when, and from which state. */
export function AuditPage() {
  const [entityId, setEntityId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(1);

  const audit = useQuery({
    queryKey: ['audit', { entityId, entityType, page }],
    queryFn: () =>
      api.listAudit({
        entityId: entityId === '' ? undefined : entityId,
        entityType: entityType === '' ? undefined : entityType,
        page,
        pageSize: 50,
      }),
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Každý stavový přechod rezervace se zapisuje sem — kdo ho provedl, kdy a z jakého stavu."
      />

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="ID záznamu" hint="UUID rezervace">
            <Input
              value={entityId}
              onChange={(event) => {
                setEntityId(event.target.value.trim());
                setPage(1);
              }}
              placeholder="6b3e2f4e-…"
            />
          </Field>
          <Field label="Typ">
            <Select
              value={entityType}
              onChange={(event) => {
                setEntityType(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Vše</option>
              <option value="Reservation">Reservation</option>
            </Select>
          </Field>
          <div className="flex items-end">
            <Pill tone="neutral">{audit.data ? `${String(audit.data.total)} událostí` : '…'}</Pill>
          </div>
        </div>
      </Card>

      {audit.isLoading ? (
        <Skeleton className="h-64" />
      ) : (audit.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="Žádná událost" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-[13px]">
            <thead>
              <tr className="text-left">
                {['Kdy', 'Typ', 'Záznam', 'Přechod', 'Kdo', 'Detail'].map((head) => (
                  <th key={head} className="label-caps pb-2">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {audit.data?.items.map((event) => (
                <tr key={event.id} className="border-t border-[var(--border-subtle)] align-top">
                  <td className="mono py-2 text-[11px] whitespace-nowrap text-[var(--text-secondary)]">
                    {dateTime(event.occurredAt)}
                  </td>
                  <td className="py-2">
                    <Pill tone="violet">{event.entityType}</Pill>
                  </td>
                  <td className="mono py-2 text-[11px]">
                    {event.entityType === 'Reservation' ? (
                      <Link
                        to={`/reservations/${event.entityId}`}
                        className="text-[var(--accent)] hover:underline"
                      >
                        {event.entityId.slice(0, 8)}…
                      </Link>
                    ) : (
                      event.entityId
                    )}
                  </td>
                  <td className="mono py-2 text-[11px]">
                    <span className="text-[var(--text-muted)]">{event.fromState ?? '—'}</span>
                    {' → '}
                    <span className="font-semibold">{event.toState ?? '—'}</span>
                  </td>
                  <td className="py-2 text-[12px]">{event.actorEmail ?? 'systém'}</td>
                  <td className="py-2 text-[12px] text-[var(--text-secondary)]">
                    {event.detail ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {(audit.data?.total ?? 0) > 50 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
            ← Předchozí
          </Button>
          <span className="mono text-[12px] text-[var(--text-muted)]">strana {page}</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={audit.data?.hasNext !== true}
            onClick={() => setPage(page + 1)}
          >
            Další →
          </Button>
        </div>
      )}
    </>
  );
}
